import { useEffect, useRef, useState, useCallback } from 'react'
import { monitorTask } from '../utils/sseMonitor'
import type { TaskMonitorEvent } from '../utils/sseMonitor'

export type SSETaskStatus = 'idle' | 'processing' | 'extracting' | 'completed' | 'failed'

export interface UseSSETaskMonitorOptions {
  monitorUrl: string | null
  enabled?: boolean
  debug?: boolean
  onProcessing?: () => void
  onExtractingMenu?: () => void
  onCompleted?: () => void
  onFailed?: (error: string) => void
}

export interface UseSSETaskMonitorReturn {
  status: SSETaskStatus
  message: string | null
  isMonitoring: boolean
  reset: () => void
}

// Status mapping for consistent handling (moved outside to prevent recreation)
const STATUS_MAP: Record<string, SSETaskStatus> = {
  'processing': 'processing',
  'extracting menu': 'extracting',
  'completed': 'completed',
  'failed': 'failed',
}

/**
 * Custom hook to monitor SSE task progress
 * 
 * @param options - Configuration options for SSE monitoring
 * @returns Current status, message, and monitoring state
 * 
 * @example
 * const { status, message, isMonitoring } = useSSETaskMonitor({
 *   monitorUrl: 'https://api.example.com/task/monitor?taskId=123',
 *   enabled: true,
 *   onCompleted: () => console.log('Task completed!'),
 *   onFailed: (error) => console.error('Task failed:', error)
 * })
 */
export const useSSETaskMonitor = (
  options: UseSSETaskMonitorOptions
): UseSSETaskMonitorReturn => {
  const {
    monitorUrl,
    enabled = true,
    debug = true,
    onProcessing,
    onExtractingMenu,
    onCompleted,
    onFailed,
  } = options

  console.log(monitorUrl,"monitorUrl");

  const [status, setStatus] = useState<SSETaskStatus>('idle')
  const [message, setMessage] = useState<string | null>(null)
  const [isMonitoring, setIsMonitoring] = useState(false)
  
  const cleanupRef = useRef<(() => void) | null>(null)
  const isCompletedRef = useRef(false)

  // Centralized cleanup function (DRY principle)
  const cleanupSSE = useCallback(() => {
    if (cleanupRef.current) {
      cleanupRef.current()
      cleanupRef.current = null
    }
    setIsMonitoring(false)
    isCompletedRef.current = true
  }, [])

  // Reset function to allow restarting monitoring
  const reset = useCallback(() => {
    isCompletedRef.current = false
    setStatus('idle')
    setMessage(null)
    setIsMonitoring(false)
  }, [])

  useEffect(() => {
    // Don't start monitoring if disabled, no URL, or already completed
    if (!enabled || !monitorUrl || isCompletedRef.current) {
      return
    } 

    setIsMonitoring(true)
    setStatus('idle')

    // Start SSE monitoring
    const cleanup = monitorTask(
      monitorUrl,
      (event: TaskMonitorEvent) => {
        // Ignore events if already completed
        if (isCompletedRef.current) return

        setMessage(event.message || null)

        // Map TaskMonitorEvent status to SSETaskStatus
        const newStatus = STATUS_MAP[event.status]

        if (!newStatus) return // Silently ignore unknown statuses

        setStatus(newStatus)

        // Handle status-specific logic using switch for clarity
        switch (newStatus) {
          case 'processing':
            onProcessing?.()
            break

          case 'extracting':
            onExtractingMenu?.()
            break

          case 'completed':
            onCompleted?.()
            cleanupSSE()
            break

          case 'failed':
            onFailed?.(event.message || 'Unknown error')
            cleanupSSE()
            break
        }
      },
      (error: Error) => {
        if (isCompletedRef.current) {
          return
        }
        
        setStatus('failed')
        setMessage(error.message || 'Connection error')
        onFailed?.(error.message || 'Connection error')
        cleanupSSE()
      }
    )

    // Store cleanup function
    cleanupRef.current = cleanup

    // Cleanup on unmount or when dependencies change
    return () => {
      if (cleanupRef.current && !isCompletedRef.current) {
        cleanupRef.current()
        cleanupRef.current = null
      }
    }
  }, [monitorUrl, enabled, debug, onProcessing, onExtractingMenu, onCompleted, onFailed, cleanupSSE])

  return {
    status,
    message,
    isMonitoring,
    reset,
  }
}
